import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { formatDate } from "@/lib/dates";
import { fmtMoney } from "@/lib/time";
import { DateInput } from "@/components/DateInput";

const STATUS_BADGE = {
  unpaid: "bg-slate-100 text-slate-600 border-slate-200",
  partial: "bg-amber-50 text-amber-800 border-amber-200",
  paid: "bg-green-50 text-green-700 border-green-200",
  overdue: "bg-red-50 text-red-700 border-red-200",
};

const thCls = "text-left text-[11px] uppercase tracking-wide text-slate-500 font-semibold px-3 py-2";
const tdCls = "px-3 py-2 text-sm text-slate-700 border-t border-slate-100";

export default function Receivables() {
  const { t } = useT();
  const [params] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [client, setClient] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [status, setStatus] = useState(params.get("status") || "");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [outstandingOnly, setOutstandingOnly] = useState(params.get("outstanding") === "1");
  const [source, setSource] = useState(params.get("source") || "");

  const load = useCallback(async () => {
    const { data } = await api.get("/receivables", {
      params: {
        client, invoice_no: invoiceNo, status, source,
        date_from: dateFrom || "", date_to: dateTo || "",
        outstanding_only: outstandingOnly ? "true" : "",
      },
    });
    setRows(data);
  }, [client, invoiceNo, status, dateFrom, dateTo, outstandingOnly, source]);

  useEffect(() => {
    const timer = setTimeout(() => load().catch(() => setRows([])), 200);
    return () => clearTimeout(timer);
  }, [load]);

  const totals = (rows || []).reduce(
    (acc, r) => ({
      gross: acc.gross + r.gross,
      paid: acc.paid + r.total_paid,
      outstanding: acc.outstanding + r.outstanding,
    }),
    { gross: 0, paid: 0, outstanding: 0 }
  );

  return (
    <div className="space-y-6" data-testid="receivables-page">
      <div>
        <h1 className="font-serif text-3xl text-slate-900" data-testid="receivables-title">{t("recv.title")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("recv.subtitle")}</p>
      </div>

      <div className="flex flex-wrap gap-3 items-center" data-testid="receivables-filters">
        <input value={client} onChange={(e) => setClient(e.target.value)} placeholder={t("recv.filter_client")}
          data-testid="recv-filter-client"
          className="px-3 py-2 border border-slate-200 rounded-md text-sm w-48 focus:outline-none focus:ring-2 focus:ring-slate-800" />
        <input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder={t("recv.filter_no")}
          data-testid="recv-filter-no"
          className="px-3 py-2 border border-slate-200 rounded-md text-sm w-40 focus:outline-none focus:ring-2 focus:ring-slate-800" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="recv-filter-status"
          className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-800">
          <option value="">{t("recv.all")}</option>
          <option value="unpaid">{t("pay.status.unpaid")}</option>
          <option value="partial">{t("pay.status.partial")}</option>
          <option value="paid">{t("pay.status.paid")}</option>
          <option value="overdue">{t("pay.status.overdue")}</option>
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="recv-filter-source"
          className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-800">
          <option value="">{t("recv.source.all")}</option>
          <option value="rplaw">{t("recv.source.rplaw")}</option>
          <option value="isaf">{t("recv.source.isaf")}</option>
        </select>
        <div className="w-36"><DateInput testid="recv-filter-date-from" value={dateFrom} onChange={setDateFrom} /></div>
        <div className="w-36"><DateInput testid="recv-filter-date-to" value={dateTo} onChange={setDateTo} /></div>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={outstandingOnly} onChange={(e) => setOutstandingOnly(e.target.checked)}
            data-testid="recv-outstanding-only" className="rounded border-slate-300" />
          {t("recv.outstanding_only")}
        </label>
      </div>

      {rows === null ? null : rows.length === 0 ? (
        <div className="text-sm text-slate-500 py-12 text-center bg-white border border-slate-200 rounded-lg" data-testid="recv-empty">
          {t("recv.empty")}
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white" data-testid="receivables-table">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className={thCls}>{t("recv.col_invoice")}</th>
                <th className={thCls}>{t("recv.filter_source")}</th>
                <th className={thCls}>{t("recv.col_client")}</th>
                <th className={thCls}>{t("recv.col_date")}</th>
                <th className={thCls}>{t("recv.col_due")}</th>
                <th className={`${thCls} text-right`}>{t("recv.col_gross")}</th>
                <th className={`${thCls} text-right`}>{t("recv.col_paid")}</th>
                <th className={`${thCls} text-right`}>{t("recv.col_outstanding")}</th>
                <th className={thCls}>{t("recv.col_status")}</th>
                <th className={`${thCls} text-right`}>{t("recv.col_days_overdue")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`recv-row-${r.id}`} className={r.payment_status === "overdue" ? "bg-red-50/40" : ""}>
                  <td className={tdCls}>
                    <Link to={r.source === "isaf" ? `/imported-invoices/${r.id}` : `/invoices/${r.id}`}
                      data-testid={`recv-link-${r.invoice_number}`}
                      className="text-amber-700 hover:underline font-semibold">
                      {r.invoice_number}
                    </Link>
                  </td>
                  <td className={tdCls}>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${r.source === "isaf" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}
                      data-testid={`recv-source-${r.invoice_number}`}>
                      {r.source === "isaf" ? t("recv.source.isaf") : "RPLAW"}
                    </span>
                  </td>
                  <td className={tdCls}>{r.client}</td>
                  <td className={tdCls}>{formatDate(r.invoice_date)}</td>
                  <td className={tdCls}>{formatDate(r.due_date)}</td>
                  <td className={`${tdCls} text-right`}>{fmtMoney(r.gross)}</td>
                  <td className={`${tdCls} text-right`}>{fmtMoney(r.total_paid)}</td>
                  <td className={`${tdCls} text-right font-semibold ${r.outstanding > 0 ? "text-amber-700" : "text-slate-500"}`}
                    data-testid={`recv-outstanding-${r.invoice_number}`}>
                    {fmtMoney(r.outstanding)}
                  </td>
                  <td className={tdCls}>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_BADGE[r.payment_status]}`}
                      data-testid={`recv-status-${r.invoice_number}`}>
                      {t(`pay.status.${r.payment_status}`)}
                    </span>
                  </td>
                  <td className={`${tdCls} text-right ${r.days_overdue > 0 ? "text-red-700 font-semibold" : ""}`}
                    data-testid={`recv-days-${r.invoice_number}`}>
                    {r.days_overdue > 0 ? r.days_overdue : "—"}
                  </td>
                </tr>
              ))}
              <tr className="bg-slate-50 font-semibold" data-testid="recv-totals">
                <td className={tdCls} colSpan={5}>{t("clients.totals")}</td>
                <td className={`${tdCls} text-right`} data-testid="recv-total-gross">{fmtMoney(totals.gross)}</td>
                <td className={`${tdCls} text-right`} data-testid="recv-total-paid">{fmtMoney(totals.paid)}</td>
                <td className={`${tdCls} text-right`} data-testid="recv-total-outstanding">{fmtMoney(totals.outstanding)}</td>
                <td className={tdCls} colSpan={2}></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
